import {
    useCallback,
    useMemo,
} from 'react'

import useEnumValues from 'app/hooks/useEnumValues'
import useLabel from 'app/hooks/useLabel'
import SkyNetSpreadSheet, {
    getEditedItems,
    SkyNetSpreadSheetControlPanel,
} from 'app/shared-components/SkyNetSpreadSheet'
import {
    AsyncMutationOptions,
    RequestConfig,
} from 'app/types/request.types'
import {
    FilterOptions, FilterContext, FilterContextType,
} from 'app/hooks/useFilter'

import useSyncState from 'app/hooks/useSyncState'

import {
    StandardBaseLeaseFilterRequestResponse,
} from 'app/requests/RateCard/RateCard.types'
import getConfig from './BaseLeaseSpreadsheet.config'
import baseLeaseFilterConfig from './BaseLeaseSpreadsheet.filterConfig'

export default function BaseLeaseSpreadsheet({
    filterOptions,
    onSave,
    onCancel,
    filterConfigRequest,
}: Readonly<{
    filterOptions: FilterOptions<StandardBaseLeaseFilterRequestResponse[]>,
    onSave?: (v: StandardBaseLeaseFilterRequestResponse[], o?: AsyncMutationOptions) => void,
    onCancel?: (...args: any[]) => void,
    filterConfigRequest: (params: { fieldName: string }) => RequestConfig,
}>) {
    const getAllowedValues = useEnumValues()
    const getLabel = useLabel()
    const {
        value, setValue,
    } = useSyncState<StandardBaseLeaseFilterRequestResponse[]>(filterOptions?.filteredData)

    const editedItems = useMemo(() => {
        return getEditedItems({
            allItems: value,
            initData: filterOptions?.filteredData,
        })
    }, [
        filterOptions?.filteredData,
        value,
    ])

    const handleSave = useCallback(async (options?: { onSuccess?: (...a: any) => void }) => {
        return onSave(editedItems, options)
    }, [
        onSave,
        editedItems,
    ])

    const config = useMemo(() => {
        return getConfig({
            getLabel,
            getAllowedValues,
        })
    }, [
        getLabel,
        getAllowedValues,
    ])

    const edited = Boolean(editedItems.length)

    const saveBeforeFilter = useCallback((setFilter) => {
        handleSave({
            onSuccess: setFilter,
        })
    }, [handleSave])

    const filterConfig: FilterContextType = useMemo(() => {
        return {
            ...filterOptions,
            config: baseLeaseFilterConfig,
            request: filterConfigRequest,
            beforeFilterCallback: saveBeforeFilter,
            edited,
        }
    }, [
        edited,
        filterConfigRequest,
        filterOptions,
        saveBeforeFilter,
    ])

    return (
        <FilterContext.Provider value={filterConfig}>
            <SkyNetSpreadSheetControlPanel
                onSave={handleSave}
                onCancel={onCancel}
                edited={edited}
            >
                <SkyNetSpreadSheet
                    config={config}
                    value={value}
                    disabled={false}
                    onChange={setValue}
                />
            </SkyNetSpreadSheetControlPanel>
        </FilterContext.Provider>
    )
}
